import { useAuth } from 'react-oidc-context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { DEBOUNCE_TIME, EDGE_API } from '~/lib/constants';
import { SavedAddressResponse } from '~/lib/types/edge-api-types';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { captureWarning } from '~/lib/metrics';

export const DEFAULT_SAVED_ADDRESSES_TAKE = 20;
export const DEFAULT_SAVED_ADDRESSES_SKIP = 0;
export const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const savedAddressesBaseKey = (tenant: string) => `${EDGE_API}/${tenant}/order-process/saved-addresses/saved`;

export const savedAddressesKey = (tenant: string, take: number, skip: number, query?: string) =>
  query
    ? `${savedAddressesBaseKey(tenant)}?take=${take}&skip=${skip}&query=${encodeURIComponent(query)}`
    : `${savedAddressesBaseKey(tenant)}?take=${take}&skip=${skip}`;

export default function useSavedAddresses(isoCode?: string, query?: string) {
  const auth = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();
  const { track } = useAnalytics();

  const [val, setVal] = useState(query);
  const take = DEFAULT_SAVED_ADDRESSES_TAKE;
  const skip = DEFAULT_SAVED_ADDRESSES_SKIP;

  useEffect(() => {
    const t = setTimeout(() => {
      setVal(query);
    }, DEBOUNCE_TIME);
    return () => clearTimeout(t);
  }, [query]);

  const key = useMemo(
    () =>
      !auth.isLoading && auth.isAuthenticated && auth.user && auth.user.access_token
        ? savedAddressesKey(tenant, take, skip, val || undefined)
        : null,
    [auth.isAuthenticated, auth.isLoading, auth.user, skip, take, tenant, val]
  );

  const { data, error, isLoading, mutate } = useSWR<SavedAddressResponse>(
    key,
    (url: string) =>
      authenticatedFetchWithTenantAnalytics(auth.user, tenant, settings, session, url)
        .then((r) => r.json())
        .then((r) => {
          if (query) {
            track({ type: 'saved_addresses_searched', payload: { query: query } });
          }
          return r;
        })
        .catch(() =>
          captureWarning('Failed to fetch saved addresses', { section: 'checkout', action: 'saved-addresses' })
        ),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: true,
      dedupingInterval: FIVE_MINUTES_MS,
      errorRetryInterval: FIVE_MINUTES_MS,
      keepPreviousData: true
    }
  );

  const onDelete = useCallback(
    async (addressId: number | undefined) => {
      if (addressId === undefined) return;
      await authenticatedFetchWithTenantAnalytics(
        auth.user,
        tenant,
        settings,
        session,
        `${EDGE_API}/${tenant}/order-process/saved-addresses/saved/${addressId}`,
        {
          method: 'DELETE'
        }
      );
      track({ type: 'saved_addresses_deleted', payload: { id: addressId } });
      mutate();
    },
    [auth, tenant, settings, session, mutate, track]
  );

  const addresses = useMemo(() => {
    return (data?.result?.addresses || []).filter((a) => !isoCode || a.country?.iso3Code === isoCode);
  }, [data, isoCode]);

  const defaultAddress = useMemo(() => {
    return addresses?.find((a) => a.isDefault);
  }, [addresses]);

  const account = useMemo(() => {
    if (auth.isAuthenticated && auth.user) {
      return {
        forename: auth.user.profile.given_name,
        surname: auth.user.profile.family_name,
        email: auth.user.profile.email || auth.user.profile.preferred_username
      };
    }
  }, [auth.isAuthenticated, auth.user]);

  return {
    defaultAddress,
    addresses,
    account,
    loading: isLoading,
    error: error,
    onDelete
  };
}
